<template>
  <div  :class="fullView ? 'full-view' : 'eot-per-finished-claims-echarts'">
    <v-chart class="chart" :option="option" ref="chartRef" :init-options="{ renderer: 'svg' }"/>
  </div>
</template>

<script>
import { use } from 'echarts/core'
import { BarChart } from 'echarts/charts'
import {
  TooltipComponent,
  GridComponent,
  LegendComponent
} from 'echarts/components'
import { SVGRenderer } from 'echarts/renderers'
import VChart from 'vue-echarts'
import { useChartResize } from '@/composables/useChartResize'
import { capitalizeFirstLetter, COLOR_PALETTE, getBarNoDataOptions, getDefaultChartTextStyle } from '@/helpers'
import { emitChartFinished } from '@/utils/insightsPdfUtils'

use([TooltipComponent, GridComponent, LegendComponent, BarChart, SVGRenderer])

export default {
  name: 'EotPerFinalizedClaimsChart',
  components: {
    VChart
  },
  props: {
    resultSet: {
      type: Object,
      required: true
    },
    fullView: {
      type: Boolean
    }
  },
  setup () {
    const { chartRef, handleResize } = useChartResize()

    return {
      chartRef,
      handleResize
    }
  },
  data () {
    return {
      option: {
        animation: !this.fullView,
        color: COLOR_PALETTE,
        title: {
          text: this.$t('insights.charts.eotPerFinishedClaims.title'),
          top: 0,
          left: 'center',
          textStyle: getDefaultChartTextStyle(this.fullView)
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: [],
          axisLabel: {
            interval: 0,
            rotate: 90,
            fontSize: 10,
            show: this.fullView
          }
        },
        yAxis: {
          type: 'value',
          name: 'EOT (days)',
          axisLabel: {
            formatter: '{value}'
          }
        },
        series: [
          {
            name: this.$t('insights.charts.eotPerFinishedClaims.seriesName'),
            type: 'bar',
            barWidth: '50%',
            data: [],
            colorBy: 'data'
          }
        ]
      }
    }
  },
  methods: {
    updateData () {
      const noDataOptions = getBarNoDataOptions(this.resultSet, this.fullView, this.option)
      if (noDataOptions) {
        this.option = {
          ...this.option,
          ...noDataOptions,
          title: this.option.title
        }
        emitChartFinished(this, true)
        return
      }

      const pivot = this.resultSet.chartPivot()
      const rawData = pivot
        .map(row => {
          let contract = row.xValues?.[0] || 'Unknown Contract'
          contract = capitalizeFirstLetter(contract)

          const variation = row.xValues?.[2] || 'Unnamed Variation'
          const eotDays = parseFloat(row['variations_parts.eotagreed']) || 0

          return {
            contract,
            variation,
            label: `${contract} - ${variation}`,
            eotDays
          }
        })
        .filter(d => d.eotDays > 0)

      rawData.sort((a, b) => a.contract.localeCompare(b.contract))

      this.option.xAxis.data = rawData.map(d => {
        return d.contract + ' - ' + d.variation
      })

      this.option.tooltip = {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      }

      this.option.series[0].data = rawData.map(d => ({
        value: d.eotDays,
        name: d.contract,
        contract: d.contract,
        variation: d.variation,
        eotDays: d.eotDays
      }))

      this.option.series[0].colorBy = 'data'
      emitChartFinished(this, false)
    }
  },
  watch: {
    resultSet: {
      handler () {
        this.updateData()
      },
      deep: true
    }
  },
  mounted () {
    this.updateData()
  }

}
</script>

<style scoped>
.eot-per-finished-claims-echarts {
  height: 250px;
}
.full-view {
  height: var(--full-view-chart-height);
  width: var(--full-view-chart-width);
}
</style>
